import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { Button, Col, Divider, Form, Modal, Row, Select, Table, Typography } from 'antd';
import {
	EditOutlined,
	ArrowRightOutlined,
	DownloadOutlined,
	InfoCircleFilled,
	CheckCircleFilled,
} from '@ant-design/icons';
import Input from 'antd/es/input/Input';
import { FC, useMemo, useRef, useState } from 'react';
import { useApp } from '@/App';
import RequestEditModal, { useRequestEditModal } from './RequestEditModal';
import { useModals } from '@/components/modals/ModalProvider';
import {
	SPFSearchOption,
	SearchSPFParam,
	useDownloadSPFDocumentQuery,
	useSPFSearch,
	useSPFSearchOptions,
	useSubmitSPFEditResolutionMutation,
} from '@/libs/fleet-api/services/spf';
import { useForm } from '@/libs/antd/form';
import { useUserData } from '@/libs/fleet-api/hooks';
import { uploader } from '@/libs/fleet-api/uploader';
import { useUploadSPFWithMKL, UploadSPFWithMKLResponse } from '@/libs/fleet-api/services/mkl';

const SPFListPage: FC = () => {
	const { navigate, notification } = useApp();
	const { modal, openCustomerDetail, openProspectDetail } = useModals();

	const { groupName } = useUserData() ?? {};
	const isReviewing = groupName === 'FBDH' || groupName === 'CCDH';

	const searchOptionQuery = useSPFSearchOptions();
	const searchOption = searchOptionQuery.data?.data ?? ({} as SPFSearchOption);

	const requestEditModal = useRequestEditModal();
	const [searchParam, setSearchParam] = useState<SearchSPFParam>({
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	});

	const searchQuery = useSPFSearch(searchParam);
	const submitSPFEditResolutionMutation = useSubmitSPFEditResolutionMutation();

	const dataSource =
		searchQuery.data?.data?.items
			?.filter((item) => {
				// For FCOH users, only show records that allow upload
				if (groupName === 'FCOH') {
					return item.allowUpload;
				}
				// For other users, show all records
				return true;
			})
			.map((item, index) => {
				return {
					...item,
					key: index + 1,
				};
			}) ?? [];

	const isLoading = searchQuery.isFetching || searchOptionQuery.isFetching;

	const { form, fieldName, value } = useForm<{
		searchBy?: string;
		keyword?: string;
		plafonTypeId?: number;
		financingLimitTypeId?: number;
		spfStatusId?: number;
		customerStatusId?: number;
		applicationStatusId?: number;
	}>();

	const disableSubmit = useMemo(() => {
		if (value?.searchBy === 'plafonTypeOptions') {
			return !value.plafonTypeId;
		}

		if (value?.searchBy === 'financingLimitTypeOptions') {
			return !value.financingLimitTypeId;
		}

		if (value?.searchBy === 'spfStatusOptions') {
			return !value.spfStatusId;
		}

		if (value?.searchBy === 'customerStatusOptions') {
			return !value.customerStatusId;
		}

		if (value?.searchBy === 'applicationStatusOptions') {
			return !value.applicationStatusId;
		}

		return false;
	}, [value]);

	const downloadSPFDocumentQuery = useDownloadSPFDocumentQuery();
	const uploadSPFWithMKL = useUploadSPFWithMKL();

	const [confirmModal, setConfirmModal] = useState<{
		visible: boolean;
		file: File;
		fileUrl: string;
		spfId: number;
		customerId: string;
		spfNo?: string;
	} | null>(null);
	const [isUploading, setIsUploading] = useState(false);

	const [successModal, setSuccessModal] = useState<{
		visible: boolean;
		result: UploadSPFWithMKLResponse;
		spfNo?: string;
	} | null>(null);

	const fileInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 20,
			}}
		>
			<FleetTitle>Pemberitahuan Fasilitas</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ flex: 'none', style: { width: 150 } }}
				onFinish={(values) => {
					if (
						values?.searchBy === 'customer-name' ||
						values?.searchBy === 'group-bmpp'
					) {
						setSearchParam({
							pagination: searchParam.pagination,
							search:
								values.keyword && values.searchBy
									? {
										by: values!.searchBy!,
										value: values!.keyword!,
									}
									: undefined,
						});
					} else {
						delete values.searchBy;
						setSearchParam({
							pagination: searchParam.pagination,
							...values,
						});
					}
				}}
			>
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchBy')}
							initialValue="customer-name"
							label="Kata Kunci"
						>
							<Select
								loading={isLoading}
								disabled={isLoading}
								options={[
									{
										label: 'Nama Customer',
										value: 'customer-name',
									},
									{
										label: 'Group BMPP',
										value: 'group-bmpp',
									},
									{
										label: 'Jenis Plafond',
										value: 'plafonTypeOptions',
									},
									{
										label: 'Sifat Limit',
										value: 'financingLimitTypeOptions',
									},
									{
										label: 'Status SPF',
										value: 'spfStatusOptions',
									},
									{
										label: 'Status Debitur',
										value: 'customerStatusOptions',
									},
									{
										label: 'Status Pengajuan',
										value: 'requestStatusOptions',
									},
									...(searchOption.frmOptions?.length
										? [
											{
												label: 'FRM',
												value: 'frmOptions',
											},
										]
										: []),
									...(searchOption.fbdhOptions?.length
										? [
											{
												label: 'FBDH',
												value: 'fbdhOptions',
											},
										]
										: []),
								]}
								style={{ width: 200 }}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						{value?.searchBy === 'customer-name' ||
							value?.searchBy === 'group-bmpp' ? (
							<Form.Item name="keyword">
								<Input
									placeholder="Search"
									disabled={isLoading}
								/>
							</Form.Item>
						) : (
							<Form.Item
								name={
									{
										plafonTypeOptions: 'plafonTypeId',
										financingLimitTypeOptions: 'financingLimitTypeId',
										spfStatusOptions: 'spfStatusId',
										customerStatusOptions: 'customerStatusId',
										requestStatusOptions: 'requestStatusId',
										fbdhOptions: 'fbdhId',
										frmOptions: 'frmId',
									}[value?.searchBy as unknown as string]
								}
							>
								<Select
									loading={isLoading}
									disabled={isLoading}
									options={
										searchOption?.[
											value?.searchBy as keyof SPFSearchOption
										]?.map((data) => {
											return {
												label: data.value,
												value: data.id,
											};
										}) ?? []
									}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						)}
					</Col>
					<Col
						flex="none"
						style={{ width: 190 }}
					>
						<Button
							block
							type="primary"
							htmlType="submit"
							disabled={isLoading || disableSubmit}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />

			<Table
				pagination={{
					total: searchQuery.data?.data.totalCount,
					onChange: (pageNumber) => {
						setSearchParam((prev) => ({
							...prev,
							pagination: {
								...prev.pagination,
								pageNumber,
							},
						}));
					},
				}}
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						render: (_, __, index) =>
							index +
							1 +
							searchParam.pagination.pageSize *
							(searchParam.pagination.pageNumber - 1),
					},
					{
						title: 'Prospect ID',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(record.prospectId);
								}}
							>
								{record.prospectId}
							</a>
						),
					},
					{
						title: 'NAP ID',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!record.prospectId) {
										notification.warning({
											message: 'Cannot navigate to NAP',
											description: 'Prospect ID is missing for this record.',
										});
										return;
									}

									navigate('NAPPage', {
										prospectId: record.prospectId,
										onBack: () => {
											navigate('SPFListPage');
										},
									});
								}}
								style={{
									color: record.prospectId ? '#1677ff' : '#bababa',
									cursor: record.prospectId ? 'pointer' : 'not-allowed',
									textDecoration: record.prospectId ? 'underline' : 'none',
								}}
							>
								{record.napNo}
							</a>
						),
					},
					{
						title: 'No. SPF',
						render: (_, record) => {
							// Make SPF number hyperlink when fully approved from CCDH
							const isFullyApproved = record.allowDownload && record.spfNo;

							if (isFullyApproved) {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (!record.id) return;

											navigate('SPFPage', {
												prospectId: record.prospectId,
												spfId: record.id,
												napResolutionId: record.napResolutionId,
												onBack: () => navigate('SPFListPage'),
												viewOnly: true,
											});
										}}
									>
										{record.spfNo}
									</a>
								);
							}

							return record.spfNo || '-';
						},
					},
					{
						title: 'Nama Customer',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.bmppGroupName,
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) =>
							record.plafonTypes
								.filter((type) => type.value)
								.map((type) => type.value)
								.join(', '),
					},
					{
						title: 'Sifat Limit',
						render: (_, record) =>
							record.financingLimitTypes.map((type) => type.value).join(', '),
					},
					{
						hidden:
							groupName !== 'FBDH' &&
							groupName !== 'CCA' &&
							groupName !== 'CCDH',
						title: 'FRM',
						render: (_, record) => record.frmName,
					},
					{
						hidden: groupName !== 'CCA' && groupName !== 'CCDH',
						title: 'FBDH',
						render: (_, record) => record.fbdhName,
					},
					{
						hidden: groupName !== 'CCDH',
						title: 'CCA',
						render: (_, record) => record.ccaName,
					},
					{
						title: 'Status SPF',
						render: (_, record) => record.spfStatus?.value,
					},
					{
						title: 'Status Debitur',
						render: (_, record) => record.customerStatus?.value,
					},
					{
						title: 'Status Pengajuan',
						render: (_, record) => record.requestStatus?.value,
					},
					{
						title: 'Status',
						render: (_, record) => {
							// Show "new" status for all groups when applicationStatus is empty
							// This ensures status remains "new" after FBDH approval until CCDH approves
							if (
								!record.applicationStatus ||
								!record.applicationStatus.value
							) {
								return 'new';
							}
							return record.applicationStatus?.value || '-';
						},
					},
					{
						hidden:
							isReviewing || !['CCA', 'FRM', 'FBDH'].includes(groupName || ''),
						align: 'center',
						title: 'Action',
						render: (_, record) => {
							// For CCA users, show download button when available
							if (groupName === 'CCA') {
								return (
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											textAlign: 'center',
										}}
									>
										{record.allowDownload && (
											<a
												href="#"
												onClick={async (e) => {
													e.preventDefault();
													if (!record.id) return;
													const { data } =
														await downloadSPFDocumentQuery.mutateAsync(
															record.id,
														);

													const link = document.createElement('a');
													link.href = data.signedUrl;
													document.body.appendChild(link);

													link.click();
													document.body.removeChild(link);
												}}
											>
												Download SPF
											</a>
										)}
									</div>
								);
							}

							// For other groups (FRM, FBDH, etc.), show original action buttons
							// Check if edit resolution is in progress using existing fields
							// If allowEditResolution is false AND there's no SPF number AND has application status
							// const isEditResolutionInProgress =
							// 	!record.allowEditResolution &&
							// 	!record.spfNo &&
							// 	record.applicationStatus &&
							// 	!record.allowInput;

							// Disable all actions if edit resolution is in progress
							// if (isEditResolutionInProgress) {
							// 	return (
							// 		<span style={{ color: '#bababa', textAlign: 'center' }}>
							// 			Edit Resolution In Progress
							// 		</span>
							// 	);
							// }

							if (record.allowDownload || record.allowUpload) {
								return (
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											textAlign: 'center',
										}}
									>
										{/* Show download if allowed */}
										{record.allowDownload && (
											<a
												href="#"
												onClick={async (e) => {
													e.preventDefault();
													if (!record.id) return;
													const { data } =
														await downloadSPFDocumentQuery.mutateAsync(
															record.id,
														);

													const link = document.createElement('a');
													link.href = data.signedUrl;
													document.body.appendChild(link);

													link.click();
													document.body.removeChild(link);
												}}
											>
												Download SPF
											</a>
										)}

										{/* Show upload if allowed */}
										{record.allowUpload && (
											<label
												style={{
													color: '#1677ff',
													cursor: 'pointer',
													marginTop:
														record.allowDownload && record.allowUpload
															? '8px'
															: '0',
												}}
											>
												<span>Upload SPF</span>
												<input
													type="file"
													hidden
													accept=".pdf"
													ref={(el) => {
														if (el && record.id) {
															fileInputRefs.current.set(record.id, el);
														}
													}}
													onChange={(e) => {
														const file = e.target.files?.[0];
														if (!file) return;

														e.target.value = '';

														const fileUrl = URL.createObjectURL(file);
														setConfirmModal({
															visible: true,
															file,
															fileUrl,
															spfId: record.id!,
															customerId: record.customerId,
															spfNo: record.spfNo,
														});
													}}
												/>
											</label>
										)}
									</div>
								);
							}

							// Check if SPF is inactive due to deviation
							// const isInactiveDueToDeviation =
							// 	record.spfStatus?.value?.toLowerCase() === 'inactive';

							// Show Input SPF only when allowed by API
							if (record.allowInput) {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											navigate('SPFPage', {
												prospectId: record.prospectId,
												napResolutionId: record.napResolutionId,
												spfId: record.id,
												allowDeviation: false,
											});
										}}
										style={{ color: '#1677ff' }}
									>
										Input SPF
									</a>
								);
							} else if (!record.allowInput && groupName === 'FRM') {
								return (
									<span style={{ color: '#bababa', textAlign: 'center' }}>
										Input SPF
									</span>
								);
							}

							return '-';
						},
					},
					{
						hidden: isReviewing || groupName === 'CCA',
						title: 'Deviasi',
						align: 'center',
						render: (_, record) => {
							const disabled = !record.id || !record.allowDeviation;
							const isInactiveDueToDeviation =
								record.spfStatus?.value?.toLowerCase() === 'inactive';

							// Show inactive message for deviated SPFs
							if (isInactiveDueToDeviation) {
								return (
									<span style={{ color: '#bababa', textAlign: 'center' }}>
										Inactive (Deviated)
									</span>
								);
							}

							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (disabled) return;

										navigate('SPFPage', {
											prospectId: record.prospectId,
											napResolutionId: record.napResolutionId,
											spfId: record.id,
											allowDeviation: true,
										});
									}}
								>
									<EditOutlined style={disabled ? { color: '#bababa' } : {}} />
								</a>
							);
						},
					},
					{
						hidden: isReviewing || groupName === 'CCA',
						title: 'Request Edit Risalah',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							// Disable if: not allowed by API, OR record is in approval process (no SPF number and has application status), OR edit resolution is already in progress
							const isEditResolutionInProgressForRequest =
								!record.allowEditResolution &&
								!record.spfNo &&
								record.applicationStatus;

							// Check if item is in approval process that should block FRM actions
							// Block when: no SPF number, has application status, and status is not 'returned' or 'completed'
							// This ensures that when CCDH returns to CCA, FRM cannot perform actions until approval continues
							const isInBlockingApprovalProcessForRequest =
								!record.spfNo &&
								record.applicationStatus &&
								record.applicationStatus.value &&
								record.applicationStatus.value !== 'completed' &&
								record.applicationStatus.value !== 'returned';

							const disabled =
								!record.allowEditResolution ||
								!record.id ||
								isInBlockingApprovalProcessForRequest ||
								isEditResolutionInProgressForRequest;
							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled) return;

										const value = await requestEditModal.open();
										if (!value) return;

										submitSPFEditResolutionMutation.mutateAsync({
											...value,
											id: record.id!,
										});

										await modal.success({
											title: (
												<strong style={{ fontSize: 16 }}>
													Request Edit Risalah Debitur Anda telah berhasil!
												</strong>
											),
											okText: 'BACK',
										});

										searchQuery.refetch();
									}}
								>
									<ArrowRightOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						hidden: groupName !== 'CCDH',
						title: 'Download',
						align: 'center',
						render: (_, record) => {
							// Backend controls download allowance
							const disabled = !record.allowDownload;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled || !record.id) return;

										const { data } = await downloadSPFDocumentQuery.mutateAsync(
											record.id,
										);

										const link = document.createElement('a');
										link.href = data.signedUrl;
										document.body.appendChild(link);

										link.click();
										document.body.removeChild(link);
									}}
								>
									<DownloadOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						hidden: !isReviewing && groupName !== 'CCA',
						title: 'Approval',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							// Backend controls approval allowance
							const disabled = !record.allowApprove;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled) return;

										navigate('SPFPage', {
											prospectId: record.prospectId,
											napResolutionId: record.napResolutionId,
											spfId: record.id,
										});
									}}
								>
									<ArrowRightOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
				]}
			/>

			<RequestEditModal {...requestEditModal} />

			{/* Confirmation Modal */}
			<Modal
				open={confirmModal?.visible ?? false}
				title={null}
				closable={false}
				centered
				onCancel={() => {
					if (confirmModal?.fileUrl) {
						URL.revokeObjectURL(confirmModal.fileUrl);
					}
					setConfirmModal(null);
				}}
				footer={[
					<Button
						key="cancel"
						disabled={isUploading || uploadSPFWithMKL.isPending}
						onClick={() => {
							if (confirmModal?.fileUrl) {
								URL.revokeObjectURL(confirmModal.fileUrl);
							}
							setConfirmModal(null);
						}}
						style={{ minWidth: 120 }}
					>
						TIDAK
					</Button>,
					<Button
						key="confirm"
						type="primary"
						loading={isUploading || uploadSPFWithMKL.isPending}
						onClick={async () => {
							if (!confirmModal) return;
							const fileid = Date.now().toString();
							const filepath = `${confirmModal.customerId}/spf/${confirmModal.spfId}/document`;
							try {
								setIsUploading(true);
								await uploader({ file: confirmModal.file, fileid, filepath });
								setIsUploading(false);
								const { data } = await uploadSPFWithMKL.mutateAsync({
									spfId: confirmModal.spfId,
									document: {
										id: fileid,
										name: confirmModal.file.name,
										path: filepath,
									},
								});
								URL.revokeObjectURL(confirmModal.fileUrl);
								setSuccessModal({ visible: true, result: data, spfNo: confirmModal.spfNo });
								setConfirmModal(null);
								searchQuery.refetch();
							} catch {
								setIsUploading(false);
								// error handled by fetcher
							}
						}}
						style={{ minWidth: 120 }}
					>
						YA
					</Button>,
				]}
				styles={{ footer: { textAlign: 'center' } }}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 12,
						padding: '24px 0 8px',
					}}
				>
					<InfoCircleFilled style={{ fontSize: 48, color: '#1677ff' }} />
					<p style={{ textAlign: 'center', fontWeight: 600, fontSize: 16, margin: 0 }}>
						Apakah Anda yakin dokumen yang diunggah telah sesuai?
					</p>
					{confirmModal?.fileUrl && (
						<a
							href={confirmModal.fileUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							{confirmModal.file.name}
						</a>
					)}
				</div>
			</Modal>

			{/* Success Modal */}
			<Modal
				open={successModal?.visible ?? false}
				title={null}
				closable={false}
				centered
				footer={[
					<Button
						key="close"
						style={{ minWidth: 160, borderColor: '#1677ff', color: '#1677ff' }}
						onClick={() => {
							setSuccessModal(null);
							navigate('SPFListPage');
						}}
					>
						TUTUP
					</Button>,
				]}
				styles={{ footer: { textAlign: 'center', paddingBottom: 24 } }}
				onCancel={() => setSuccessModal(null)}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						textAlign: 'center',
						gap: 12,
						padding: '32px 16px 16px',
					}}
				>
					<CheckCircleFilled style={{ fontSize: 56, color: '#52c41a' }} />
					<Typography.Text strong style={{ fontSize: 16, display: 'block' }}>
						Surat Persetujuan Fasilitas Berhasil Diunggah!
					</Typography.Text>
					<Typography.Text type="secondary" style={{ display: 'block' }}>
						Berikut adalah nomor Surat Persetujuan Fasilitas Debitur Anda yang telah dibentuk :
					</Typography.Text>
					{successModal?.spfNo && (
						<Typography.Text
							strong
							style={{ fontSize: 20, color: '#1677ff', display: 'block' }}
						>
							{successModal.spfNo}
						</Typography.Text>
					)}
					<Typography.Text type="secondary" style={{ display: 'block' }}>
						Nomor MKL Anda dapat dilihat pada menu Memo Kredit Limit.
					</Typography.Text>
				</div>
			</Modal>
		</div>
	);
};

export default SPFListPage;
